import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
// eslint-disable-next-line import/no-extraneous-dependencies
import Chart from "chart.js/auto";
import { Expandable } from "common/components/Expandable/Expandable";
import dayjs from "dayjs";
import clsx from "clsx";
import Icon from "components/Icon/Icon";
import getAnimalLabel from "utils/animalLabel";
import LegendChart, { chartCategories, getChartColors } from "./LegendChart";

function DistributionChart({
  distribution,
  comments,
  isDistributionGraphChartVisible,
  isBadCriteriaChartVisible,
  isCommentsChartVisible,
  areEventsVisible,
  areEnrichmentsVisible,
  areUpdatesVisible,
  events,
  enrichments,
  enclosureUpdates,
  ...props
}: any) {
  const { t } = useTranslation();
  const chartRef = useRef<Chart | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const radarData = useMemo(() => {
    if (!distribution) return { labels: [], datasets: [] };
    const { sections } = distribution;

    const labels = sections?.map((section: any) => section.title) || [];

    const datasets = Object.entries(chartCategories).map(([cat, _]) => ({
      label: t(`balanceChartLabels.${cat}`),
      data: sections?.map((section: any) => section[cat]) || [],
      fill: true,
      backgroundColor: "transparent",
      borderColor: getChartColors(cat).background,
      pointBackgroundColor: getChartColors(cat).background,
      pointBorderColor: "#fff",
      pointHoverBackgroundColor: "#fff",
      pointHoverBorderColor: getChartColors(cat).background,
    }));

    return { labels, datasets };
  }, [t, distribution]);

  const radarConfig = useMemo(() => {
    return {
      type: "radar",
      data: radarData,
      options: {
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (tooltipItem: any) => {
                // On récupère la valeur du point
                const value =
                  Math.round((Number(tooltipItem.raw) + Number.EPSILON) * 100) /
                  100;
                const datasetLabel = tooltipItem.dataset.label;

                // Vérifie si c'est une valeur 0 et si c'est le premier affiché
                const firstZeroIndex =
                  tooltipItem.chart.data.datasets[0].data.findIndex(
                    (v: any) => v === 0
                  );

                // Si la valeur est 0 et qu'on n'est pas sur le premier, on retourne null pour le cacher
                if (value === 0 && tooltipItem.dataIndex !== firstZeroIndex) {
                  return null;
                }

                return `${datasetLabel} : ${value}`;
              },
            },
          },
        },
        elements: {
          line: {
            borderWidth: 3,
          },
        },
      },
    };
  }, [radarData]);

  const commentsRenderKey = useMemo(() => {
    if (!comments?.length) return "no-comments";

    return JSON.stringify(
      comments.map((grid: any) => ({
        gridName: grid?.gridName,
        criteria: (grid?.criteria ?? []).map((criterion: any) => ({
          key: criterion?.key,
          comments: (criterion?.comments ?? []).map((comment: any) => ({
            comment: comment?.comment,
            author: comment?.author,
            date: comment?.date,
          })),
        })),
      }))
    );
  }, [comments]);

  useEffect(() => {
    if (distribution && isDistributionGraphChartVisible) {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }

      const ctx = canvasRef.current;
      if (!ctx) return () => {};

      chartRef.current = new Chart(ctx, radarConfig as any);

      return () => {
        chartRef.current?.destroy();
        chartRef.current = null;
      };
    }
    return () => {};
  }, [distribution, isDistributionGraphChartVisible, radarConfig]);

  return isDistributionGraphChartVisible ||
    isBadCriteriaChartVisible ||
    isCommentsChartVisible ? (
    <div className="rounded-2xl border border-slate-200 flex flex-col gap-2 p-8 pb-0 flex-1">
      {distribution?.sections && isDistributionGraphChartVisible ? (
        <h3 className="text-xl font-semibold">
          {t("balance.repartitionTitle")}
        </h3>
      ) : null}
      <div className="w-full flex flex-col gap-2">
        {distribution?.sections && isDistributionGraphChartVisible ? (
          <LegendChart />
        ) : null}
        {distribution?.sections && isDistributionGraphChartVisible ? (
          <div className="w-full max-w-[500px] flex flex-col self-center -mt-10">
            <canvas id="radar" ref={canvasRef} />
          </div>
        ) : (
          <div>
            <span>{t(`common.noDataToShow`)}</span>
          </div>
        )}
        <div className="flex flex-row justify-evenly flex-wrap gap-6">
          {distribution?.badCriteria?.length && isBadCriteriaChartVisible ? (
            <div className="flex flex-col gap-2 pb-2 flex-1">
              <h3 className="text-xl font-semibold">
                {t("balance.badResponseDistribution")}
              </h3>
              {distribution?.badCriteria?.map(
                (bc: any, badCriteriaIndex: number) => {
                  return (
                    <div
                      key={`bad-criteria-${bc?.gridName}-${badCriteriaIndex}`}
                    >
                      <Expandable
                        title={bc?.gridName}
                        contentClassName="pl-10"
                        content={
                          <div className="flex flex-col gap-2">
                            {bc?.criteria?.map(
                              (criterion: any, criterionIndex: number) => {
                                return (
                                  <div
                                    key={`bad-criterion-${criterion?.key}-${bc?.gridName}-${criterionIndex}`}
                                  >
                                    - {criterion?.key} :{" "}
                                    <span className="text-[#E14D26] font-bold">
                                      {t("balance.xTimes", {
                                        count: criterion.numberOfOccurences,
                                      })}
                                    </span>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        }
                      />
                    </div>
                  );
                }
              )}
            </div>
          ) : null}
          {comments?.length && isCommentsChartVisible ? (
            <div
              className="flex flex-col gap-2 pb-2 flex-1"
              key={commentsRenderKey}
            >
              <h3 className="text-xl font-semibold">
                {t("balance.observationComments")}
              </h3>
              {comments?.map((grid: any, gridIndex: number) => {
                return (
                  <div key={`comments-grid-${grid?.gridName}-${gridIndex}`}>
                    <Expandable
                      title={grid?.gridName}
                      contentClassName="pl-10"
                      content={
                        <div className="flex flex-col gap-6">
                          {grid?.criteria?.map(
                            (criterion: any, index: number) => {
                              return (
                                <div
                                  key={`comment-criterion-${criterion?.key}-${grid?.gridName}-${index}`}
                                  className={clsx(
                                    "pb-2",
                                    index + 1 < grid.criteria.length &&
                                      "border-b-2 border-b-slate-200"
                                  )}
                                >
                                  <Expandable
                                    title={criterion?.key}
                                    contentClassName="pl-10"
                                    rowClassName="gap-4"
                                    content={
                                      <div className="flex flex-col gap-2">
                                        {criterion?.comments?.map(
                                          (
                                            comment: any,
                                            commentIndex: number
                                          ) => {
                                            return (
                                              <div
                                                key={`comment-line-${comment?.comment}-${comment?.author}-${comment?.date}-${grid?.gridName}-${criterion?.key}-${commentIndex}`}
                                              >
                                                <span>
                                                  - {comment?.comment}
                                                  <span className="italic text-xs ml-1">
                                                    {comment?.author &&
                                                    comment?.date
                                                      ? t(
                                                          "balance.byAuthorDate",
                                                          {
                                                            author:
                                                              comment.author,
                                                            date: dayjs(
                                                              comment.date
                                                            ).format("LL"),
                                                          }
                                                        )
                                                      : null}
                                                  </span>
                                                </span>
                                              </div>
                                            );
                                          }
                                        )}
                                      </div>
                                    }
                                  />
                                </div>
                              );
                            }
                          )}
                        </div>
                      }
                    />
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
        <div className="mt-2 flex flex-col gap-2">
          {areEventsVisible && (
            <Expandable
              titleComponent={
                <h3 className="text-xl font-semibold">{t("balance.events")}</h3>
              }
              content={
                <div className="flex flex-col gap-4">
                  {events?.map((event: any) => {
                    return (
                      <div
                        key={event.id}
                        className="flex flex-row items-center gap-2"
                      >
                        <Icon name="Megaphone" className="w-6 h-6" />
                        <p>
                          <strong>
                            {event?.enclosure
                              ? event?.enclosure?.name
                              : event?.animal
                              ? getAnimalLabel(
                                  event.animal?.id,
                                  event.animal?.houseName,
                                  event.animal?.zimsId,
                                  event.animal?.localId,
                                  event.animal?.tag
                                )
                              : event?.observationGroup
                              ? `${t("common.individualsGroup")}: ${
                                  event?.observationGroup?.name
                                }`
                              : null}
                          </strong>{" "}
                          {event.shortDescription}{" "}
                          <span className="italic">
                            ({dayjs(event.date ?? event.createdAt).format("LLL")})
                          </span>
                        </p>
                      </div>
                    );
                  })}
                </div>
              }
            />
          )}
          {areEnrichmentsVisible && (
            <Expandable
              titleComponent={
                <h3 className="text-xl font-semibold">
                  {t("balance.enrichments")}
                </h3>
              }
              content={
                <div className="flex flex-col gap-4">
                  {enrichments?.map((enclosureUpdate: any) => {
                    return (
                      <div
                        key={enclosureUpdate.id}
                        className="flex flex-row items-center gap-2"
                      >
                        <Icon name="BsStar" className="w-6 h-6" />
                        <p>
                          {enclosureUpdate.type === "start"
                            ? "Début : "
                            : enclosureUpdate.type === "end"
                            ? "Fin : "
                            : ""}
                          {enclosureUpdate.enrichmentRequestTitle}{" "}
                          <span className="italic">
                            (
                            {enclosureUpdate.type === "start"
                              ? dayjs(enclosureUpdate.startDateTime).format(
                                  "LL"
                                )
                              : enclosureUpdate.type === "end"
                              ? dayjs(enclosureUpdate.endDateTime).format("LL")
                              : `Du ${dayjs(
                                  enclosureUpdate.startDateTime
                                ).format("ll")} au ${dayjs(
                                  enclosureUpdate.endDateTime
                                ).format("ll")}`}
                            )
                          </span>
                        </p>
                      </div>
                    );
                  })}
                </div>
              }
            />
          )}
          {areUpdatesVisible && (
            <Expandable
              titleComponent={
                <h3 className="text-xl font-semibold">
                  {t("balance.enclosureUpdates")}
                </h3>
              }
              content={
                <div className="flex flex-col gap-4">
                  {enclosureUpdates?.map((enclosureUpdate: any) => {
                    return (
                      <div
                        key={enclosureUpdate.id}
                        className="flex flex-row items-center gap-2"
                      >
                        <Icon name="PencilSquareIcon" className="w-6 h-6" />
                        <p>
                          {enclosureUpdate.type === "start"
                            ? "Début : "
                            : enclosureUpdate.type === "end"
                            ? "Fin : "
                            : ""}
                          {enclosureUpdate.title}{" "}
                          <span className="italic">
                            (
                            {enclosureUpdate.type === "start"
                              ? dayjs(
                                  enclosureUpdate.actualStartDateTime
                                ).format("LL")
                              : enclosureUpdate.type === "end"
                              ? dayjs(enclosureUpdate.actualEndDateTime).format(
                                  "LL"
                                )
                              : `Du ${dayjs(
                                  enclosureUpdate.actualStartDateTime
                                ).format("ll")} au ${dayjs(
                                  enclosureUpdate.actualEndDateTime
                                ).format("ll")}`}
                            )
                          </span>
                        </p>
                      </div>
                    );
                  })}
                </div>
              }
            />
          )}
        </div>
      </div>
    </div>
  ) : null;
}

export default DistributionChart;
